import { TableTextFilter } from "components/TableTextFilter/TableTextFilter";
import { setScrollBehavior } from "features/scrollPageBehaviorProvider/scrollPageBehaviorProvider";
import { _ } from "i18n";
import type { TokenMovementRowResponse } from "libs/apiClient";
import { TokenMovementType, TokenMovementSource } from "libs/apiClient";
import client from "libs/client";
import { useEffect, useState } from "react";
import DatagridV2, { type SimpleColumn } from "ui-kit/src/Datagrid/DatagridV2";
import { DiscoverBreadcrumbs } from "features/discover/DiscoverBreadcrumbs";
import {createSortArrows, formatDate, formatDateTime} from "features/discover/functions";
import SelectComponent from "ui-kit/src/SelectComponent/SelectComponent";
import { useI18n } from "features/translation/context";
import { DateTimeRangeCalendar } from "ui-kit/src/DateTimeRangeCalendar/DateTimeRangeCalendar";
import { useSearchParams } from "react-router";

export function DiscoverTokenMovementsPage() {
	const { translator, locale } = useI18n();
	const [searchParams] = useSearchParams();

	// Read clubId and teamId from URL parameters
	const clubIdFromUrl = searchParams.get('clubId');
	const teamIdFromUrl = searchParams.get('teamId');

	const movementTypeOptions = Object.keys(TokenMovementType.Values).map(value => ({
		value,
		label: translator.translateAny(`token.movement.type.${value}`)
	}));

	const sourceOptions = Object.keys(TokenMovementSource.Values).map(value => ({
		value,
		label: translator.translateAny(`token.source.${value}`)
	}));

	const [pagination, setPagination] = useState<{ pageIndex: number; pageSize: number }>({
		pageIndex: 0,
		pageSize: 15,
	});

	const [filters, setFilters] = useState<{ [key: string]: string | number | undefined }>({});
	const [filterValues, setFilterValues] = useState<{ [key: string]: string | undefined }>({});
	const [sort, setSort] = useState<{ field: string, direction: string }>();
	const [createdAtTimeRange, setCreatedAtTimeRange] = useState({ start: "", end: "" });
	const [purchasedAtTimeRange, setPurchasedAtTimeRange] = useState({ start: "", end: "" });

	useEffect(() => { setScrollBehavior() }, []);

	const { data, isLoading } = client.getTokenMovements.useQuery({
		page: pagination.pageIndex + 1,
		limit: pagination.pageSize,
		filters: [
			// URL parameter filters (hidden from UI)
			...(clubIdFromUrl ? [{
				field: "clubId",
				type: "exact",
				value: parseInt(clubIdFromUrl, 10),
			}] : []),
			...(teamIdFromUrl ? [{
				field: "teamId",
				type: "exact",
				value: parseInt(teamIdFromUrl, 10),
			}] : []),
			...(filters?.id ? [{
				field: "id",
				type: "exact",
				value: parseInt(filters.id as string, 10),
			}] : []),
			...(filters?.clubId ? [{
				field: "clubId",
				type: "exact",
				value: filters.clubId,
			}] : []),
			...(filters?.clubName ? [{
				field: "clubName",
				type: "search",
				value: filters.clubName,
			}] : []),
			...(filters?.teamId ? [{
				field: "teamId",
				type: "exact",
				value: filters.teamId,
			}] : []),
			...(filters?.teamName ? [{
				field: "teamName",
				type: "search",
				value: filters.teamName,
			}] : []),
			...(filters?.relatedClubId ? [{
				field: "relatedClubId",
				type: "exact",
				value: filters.relatedClubId,
			}] : []),
			...(filters?.relatedClubName ? [{
				field: "relatedClubName",
				type: "search",
				value: filters.relatedClubName,
			}] : []),
			...(filters?.relatedTeamId ? [{
				field: "relatedTeamId",
				type: "exact",
				value: filters.relatedTeamId,
			}] : []),
			...(filters?.relatedTeamName ? [{
				field: "relatedTeamName",
				type: "search",
				value: filters.relatedTeamName,
			}] : []),
			...(filters?.userId ? [{
				field: "userId",
				type: "exact",
				value: filters.userId,
			}] : []),
			...(filters?.userName ? [{
				field: "userName",
				type: "search",
				value: filters.userName,
			}] : []),
			...(filters?.amount ? [{
				field: "amount",
				type: "exact",
				value: filters.amount,
			}] : []),
			...(filters?.movementType ? [{
				field: "movementType",
				type: "exact",
				value: filters.movementType,
			}] : []),
			...(filters?.source ? [{
				field: "source",
				type: "exact",
				value: filters.source,
			}] : []),
			...(filters?.note ? [{
				field: "note",
				type: "search",
				value: filters.note,
			}] : []),
			...((purchasedAtTimeRange.start || purchasedAtTimeRange.end) ? [{
				field: "purchasedAt",
				type: "datetimerange",
				value: {
					start: purchasedAtTimeRange.start === "" ? undefined : purchasedAtTimeRange.start,
					end: purchasedAtTimeRange.end === "" ? undefined : purchasedAtTimeRange.end,
				},
			}] : []),
			...((createdAtTimeRange.start || createdAtTimeRange.end) ? [{
				field: "createdAt",
				type: "datetimerange",
				value: {
					start: createdAtTimeRange.start === "" ? undefined : createdAtTimeRange.start,
					end: createdAtTimeRange.end === "" ? undefined : createdAtTimeRange.end,
				},
			}] : []),
		],
		order: sort
	});

	const handleSetFilter = (key: string, value?: number | string,) => {
		setFilters({ ...filters, [key]: value });
		setPagination({ ...pagination, pageIndex: 0 });
	}

	const handleClearFilter = (key: string) => {
		setFilters({ ...filters, [key]: undefined })
	}

	const handleSort = (newSort?: { field: string, direction: string }) => {
		setSort(newSort);
	};

	const handleCreatedAtDateTimeChange = (start: string, end: string) => {
		setCreatedAtTimeRange({ start: start, end: end });
	};

	const handlePurchasedAtDateTimeChange = (start: string, end: string) => {
		setPurchasedAtTimeRange({ start: start, end: end });
	};

	const generateColumnHeader = (columnName: string, label: string, isNumber?: boolean, className?: string, noSort?: boolean) => () => (
		<div className={`tableHeader ${className}`}>
			<TableTextFilter
				value={filterValues[columnName] || ""}
				onChange={(value) => setFilterValues({ ...filterValues, [columnName]: value })}
				number={isNumber}
				clear={() => {
					handleClearFilter(columnName);
					setFilterValues({ ...filterValues, [columnName]: undefined })
				}}
				onSubmit={(value: string) => handleSetFilter(columnName, value)}
				placeholder={label}
			/>
			{!noSort && createSortArrows(columnName, sort, handleSort)}
		</div>
	);

	const simpleColumns: SimpleColumn<TokenMovementRowResponse>[] = [
		{
			id: "id",
			key: "id",
			header: generateColumnHeader("id", "ID", true, "tableHeader--id"),
			cell: (value, row) => `${value || ''}`,
		},
		{
			id: "amount",
			key: "amount",
			header: generateColumnHeader("amount", "Amount", true),
			cell: (value, row: TokenMovementRowResponse | undefined) => (
				<div style={{ color: row?.amount && row.amount > 0 ? 'green' : 'red', fontWeight: 'bold' }}>
					{row?.amount && row.amount > 0 ? `+${row.amount}` : row?.amount || 0}
				</div>
			),
		},
		{
			id: "movementType",
			key: "movementType",
			header: () => <div className="tableHeader tableHeader--select">
				<SelectComponent
					value={movementTypeOptions.find((o) => o.value === filters?.movementType)}
					handleChange={(value) => handleSetFilter("movementType", value?.value)}
					placeholder="Type"
					items={movementTypeOptions}
				/>
			</div>,
			cell: (value) => <div className="thinCell">{translator.translateAny(`token.movement.type.${value}`)}</div>,
		},
		{
			id: "clubName",
			key: "clubName",
			header: generateColumnHeader("clubName", "Club", false, ''),
			cell: (value, row) => <div className="thinCell">{value || "-"}</div>,
		},
		{
			id: "teamName",
			key: "teamName",
			header: generateColumnHeader("teamName", "Team", false, ''),
			cell: (value, row) => <div className="thinCell">{value || "-"}</div>,
		},
		{
			id: "relatedClubName",
			key: "relatedClubName",
			header: generateColumnHeader("relatedClubName", "Related Club", false, ''),
			cell: (value, row) => <div className="thinCell">{value || "-"}</div>,
		},
		{
			id: "relatedTeamName",
			key: "relatedTeamName",
			header: generateColumnHeader("relatedTeamName", "Related Team", false, ''),
			cell: (value, row) => <div className="thinCell">{value || "-"}</div>,
		},
		{
			id: "purchasedAt",
			key: "purchasedAt",
			header: () => (
				<div className="tableHeader tableHeader--calendar-left">
					<DateTimeRangeCalendar
						placeholder="Purchased"
						apiFormat
						onChange={handlePurchasedAtDateTimeChange}
						start={purchasedAtTimeRange.start}
						end={purchasedAtTimeRange.end}
						locale={locale}
					/>
					{createSortArrows("purchasedAt", sort, handleSort)}
				</div>
			),
			cell: (value, row) => <div className="thinCell">{formatDateTime(value as string | null)}</div>,
		},
		{
			id: "createdAt",
			key: "createdAt",
			header: () => (
				<div className="tableHeader tableHeader--calendar-left">
					<DateTimeRangeCalendar
						placeholder="Created"
						apiFormat
						onChange={handleCreatedAtDateTimeChange}
						start={createdAtTimeRange.start}
						end={createdAtTimeRange.end}
						locale={locale}
					/>
					{createSortArrows("createdAt", sort, handleSort)}
				</div>
			),
			cell: (value, row) => <div className="thinCell">{formatDateTime(value as string | null)}</div>,
		},
		{
			id: "userName",
			key: "userName",
			header: generateColumnHeader("userName", "User", false, ''),
			cell: (value, row) => <div className="thinCell">{value || "-"}</div>,
		},
		{
			id: "source",
			key: "source",
			header: () => <div className="tableHeader tableHeader--select">
				<SelectComponent
					value={sourceOptions.find((o) => o.value === filters?.source)}
					handleChange={(value) => handleSetFilter("source", value?.value)}
					placeholder="Source"
					items={sourceOptions}
				/>
			</div>,
			cell: (value) => <div className="thinCell">{translator.translateAny(`token.source.${value}`)}</div>,
		},
		{
			id: "note",
			key: "note",
			header: generateColumnHeader("note", "Note", false, ''),
			cell: (value, row) => <div className="thinCell">{value || "-"}</div>,
		},
	];

	return (
		<div className="discover-container discover-container--wide">
			<div className="pageHeading">
				<DiscoverBreadcrumbs items={[
					{
						label: 'Token Movements',
					},
				]} />
			</div>
			<div className="discover-card discover-card--for-table">
				<DatagridV2
					isLoading={isLoading}
					simpleColumns={simpleColumns}
					data={data?.data.items ?? []}
					pageCount={data?.data?.pages ?? 1}
					pagination={pagination}
					setPagination={setPagination}
					noResults={_("datatables.zero_records")}
				/>
			</div>
		</div>
	);
}
